import React, { forwardRef, useEffect, useRef } from 'react';
import { cn } from '@/utils';

// Mobile-optimized button variants with touch-friendly sizes
/**
 * The primary action is BLACK, not the accent — this is the direction's
 * signature move. Black buttons lighten on hover (you cannot go darker),
 * which is the inverse of the usual tint-darkening pattern.
 *
 * `link` is the one variant that spends the accent, because a link that
 * isn't colored isn't recognisably a link.
 */
const buttonVariants = {
  variant: {
    primary: 'bg-ink-700 text-white hover:bg-ink-500 focus-visible:ring-accent-600 active:bg-ink-900',
    secondary: 'bg-white text-neutral-900 border border-neutral-300 hover:border-neutral-500 focus-visible:ring-accent-600 active:bg-neutral-50',
    success: 'bg-success-600 text-white hover:bg-success-700 focus-visible:ring-success-600 active:bg-success-800',
    warning: 'bg-warning-600 text-white hover:bg-warning-700 focus-visible:ring-warning-600 active:bg-warning-800',
    danger: 'bg-danger-600 text-white hover:bg-danger-700 focus-visible:ring-danger-600 active:bg-danger-800',
    outline: 'border border-neutral-300 bg-transparent text-neutral-900 hover:border-neutral-500 focus-visible:ring-accent-600',
    ghost: 'bg-transparent text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:ring-accent-600 active:bg-neutral-200',
    link: 'bg-transparent text-accent-600 underline-offset-4 hover:underline hover:text-accent-700 focus-visible:ring-accent-600',
  },
  size: {
    sm: 'h-11 px-3 text-sm min-h-[44px] min-w-[44px]', // 44px — Apple HIG minimum tap target
    md: 'h-12 px-4 text-base min-h-[48px] min-w-[48px]', // Comfortable 48px
    lg: 'h-14 px-6 text-lg min-h-[56px] min-w-[56px]', // Large 56px
    xl: 'h-16 px-8 text-xl min-h-[64px] min-w-[64px]', // Extra large 64px
    touch: 'h-12 px-4 text-base min-h-[48px] min-w-[48px]', // Specific touch size
    icon: 'h-12 w-12 p-0 min-h-[48px] min-w-[48px]', // Square icons
  },
};

// Professional loading spinner component
const LoadingSpinner = ({ size = 16 }: { size?: number }) => (
  <svg
    className="animate-spin"
    style={{ width: size, height: size }}
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof buttonVariants.variant;
  size?: keyof typeof buttonVariants.size;
  fullWidth?: boolean;
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  haptic?: boolean;
  longPress?: boolean;
  onLongPress?: () => void;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      haptic = true,
      longPress = false,
      onLongPress,
      onClick,
      onTouchStart,
      onTouchEnd,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const longPressTimerRef = useRef<NodeJS.Timeout>();
    const touchStartRef = useRef<number>(0);
    
    const effectiveSize = size;

    const baseClasses = [
      'inline-flex items-center justify-center whitespace-nowrap',
      'font-semibold transition-colors duration-150 ease-out',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      'select-none touch-manipulation', // Optimize for touch
      'rounded',
      buttonVariants.variant[variant],
      buttonVariants.size[effectiveSize],
      fullWidth ? 'w-full' : 'w-auto',
      'active:scale-[0.98] transform-gpu',
    ];

    const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
      if (isDisabled) return;
      
      touchStartRef.current = Date.now();
      
      // Long press detection
      if (longPress && onLongPress) {
        longPressTimerRef.current = setTimeout(() => {
          onLongPress();
        }, 500);
      }
      
      onTouchStart?.(e);
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
      // Clear long press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      
      onTouchEnd?.(e);
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) return;
      
      // Prevent click if it was a long press
      if (longPress && Date.now() - touchStartRef.current > 500) {
        return;
      }
      
      onClick?.(e);
    };

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
        }
      };
    }, []);

    /*
     * asChild: put the button's styling ON the child, do not wrap it.
     *
     * This used to render a <div> carrying every button class with the child
     * nested inside a <span class="truncate">. For the common case —
     * <Button asChild><Link/></Button> — that produced a 48px-tall thing that
     * looked like a button but whose only navigable part was the 18px inline
     * <a> in the middle of it. Everything outside the text was dead pixels,
     * and a screen reader saw a link inside a decorative div rather than one
     * button-shaped control.
     *
     * Cloning onto the child makes the <a> itself the styled control: correct
     * semantics, and the whole 48px box is the tap target.
     */
    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{
        className?: string;
        children?: React.ReactNode;
      }>;

      return React.cloneElement(child, {
        ...(props as Record<string, unknown>),
        className: cn(...baseClasses, className, child.props.className),
        ref,
        onClick: handleClick,
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
        children: (
          <>
            {leftIcon && !loading && (
              <span className="mr-2 flex-shrink-0">{leftIcon}</span>
            )}
            {loading && (
              <span className="mr-2 flex-shrink-0">
                <LoadingSpinner size={effectiveSize === 'sm' ? 16 : effectiveSize === 'lg' ? 20 : effectiveSize === 'xl' ? 24 : 18} />
              </span>
            )}
            <span className="truncate">{child.props.children}</span>
            {rightIcon && !loading && (
              <span className="ml-2 flex-shrink-0">{rightIcon}</span>
            )}
          </>
        ),
      } as never);
    }

    return (
      <button
        className={cn(...baseClasses, className)}
        ref={ref}
        disabled={isDisabled}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        {...props}
      >
        {/* Left Icon */}
        {leftIcon && !loading && (
          <span className="mr-2 flex-shrink-0">{leftIcon}</span>
        )}
        
        {/* Loading Spinner */}
        {loading && (
          <span className="mr-2 flex-shrink-0">
            <LoadingSpinner size={effectiveSize === 'sm' ? 16 : effectiveSize === 'lg' ? 20 : effectiveSize === 'xl' ? 24 : 18} />
          </span>
        )}
        
        {/* Button Content */}
        <span className="truncate">{children}</span>
        
        {/* Right Icon */}
        {rightIcon && !loading && (
          <span className="ml-2 flex-shrink-0">{rightIcon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };